import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { InputLabel } from '@wajeez-web/components/ui';
import { SwitchFormType, TextFormType } from '../Types';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';

export interface FormSwitchInputProps<TFormFields extends FieldValues>
  extends UseControllerProps<TFormFields> {
  fieldType: SwitchFormType;
  disabled?: boolean;
}

const FormSwitchInput = <TFormFields extends FieldValues>({
  fieldType,
  disabled,
  ...props
}: FormSwitchInputProps<TFormFields>) => {
  const { field, fieldState } = useController({
    ...props,
    rules: {
      required: fieldType.required ? 'Required' : undefined,
      ...props.rules,
    },
  });

  const { dir } = useCommonSelector((state) => state.common.settings);

  const { t } = useTranslation();

  const isError = fieldState.error && fieldState.isTouched;

  return (
    <div className="flex flex-row justify-between align-middle">
      <div className="flex-grow flex-col">
        {fieldType.label && (
          <InputLabel
            text={
              t(
                `inputs.labels.${fieldType.label}`,
                fieldType.label.toString() || ''
              ) as string
            }
            name={field.name}
            required={fieldType.required}
          />
        )}{' '}
        <div className="text-textSecondary text-xs text-start">
          <ReturnTranslatedText
            origintext={fieldType.placeHolder}
            text={`inputs.placeholders.${fieldType.placeHolder}`}
          />{' '}
        </div>
      </div>

      <div
        onClick={(e) => field.onChange(!field.value)}
        className="relative input inline-block w-10 my-auto me-2 align-middle select-none transition duration-200 ease-in h-full"
      >
        <span
          className={`${
            field.value ? `ltr:right-0 rtl:left-0 border-primary` : ''
          } border-gray-300 absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer`}
        />
        <label
          className={`block overflow-hidden h-6 rounded-full ${
            field.value ? 'bg-primary' : 'bg-gray-300'
          } cursor-pointer`}
        ></label>
      </div>
    </div>
  );
};

export default FormSwitchInput;
